import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deleteMailAccount } from "@/lib/api/accounts";
import { clearAccountClientMailCache } from "@/lib/mail/accountMailCache";
import { useInboxBulkActions } from "@/hooks/useInboxBulkActions";
import { useInboxListDisplay } from "@/hooks/useInboxListDisplay";
import { useMailRefresh } from "@/hooks/useMailRefresh";
import { useMailSearchDisplay } from "@/hooks/useMailSearchDisplay";
import { useMailSendQueue } from "@/hooks/useMailSendQueue";
import { scheduledSendsToSummaries, useMailScheduledSends } from "@/hooks/useMailScheduledSends";
import { useMailSnooze } from "@/hooks/useMailSnooze";
import { useMessageLabels } from "@/hooks/useMessageLabels";
import { useOrderedMailAccounts } from "@/hooks/useOrderedMailAccounts";
import { useSnoozedListRows } from "@/hooks/useSnoozedListRows";
import { useMobileActions } from "@/mobile/hooks/useMobileActions";
import { useMobileCompose } from "@/mobile/hooks/useMobileCompose";
import { useMobileMail } from "@/mobile/hooks/useMobileMail";
import { useMobileMessage } from "@/mobile/hooks/useMobileMessage";
import { useMobileNavigation } from "@/mobile/hooks/useMobileNavigation";
import { warmMessageBodyForOpen } from "@/lib/mail/bodyCacheLookup";
import { clearMailDeepLinkFromUrl, readMailDeepLink } from "@/lib/mail/mailDeepLink";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import { INBOX_FILTER_DEFAULT, type InboxListFilter } from "@/lib/mail/messageLabels";
import { readInboxListFilter, writeInboxListFilter } from "@/lib/mail/inboxListFilterPrefs";
import { isDraftsNavFolder, navFolderSlot, NAV_FOLDER_LABELS, DRAFTS_SAVING_FOLDER_LABEL, resolveSentImapFolder, resolveTrashImapFolder } from "@/lib/mail/folderNavSections";
import { ensureSentFolderOverride } from "@/lib/mail/sentFolderSetup";
import {
  criticalFolderSlotsNeedAttention,
  hasSeenFolderImapNudge,
  markFolderImapNudgeSeen,
} from "@/lib/mail/folderSlotImapStatus";
import { fetchMailFolders } from "@/lib/api/imap";
import { showMailToast } from "@/lib/ui/mailToast";
import { syncFolderHeaders } from "@/lib/mail/folderWarmup";
import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import {
  AURELIA_OUTBOX_FOLDER,
  isOutboxNavFolder,
} from "@/lib/outbox/constants";
import { mergeOutboxSummaries, outboxTotalCount } from "@/lib/outbox/outboxDisplay";
import { resolveOutboxNavLabel } from "@/lib/outbox/outboxNavRows";
import { getScheduledSendsCache, upsertScheduledSendCache, removeScheduledSendPlaceholders } from "@/lib/outbox/scheduledSendsSessionCache";
import {
  AURELIA_SNOOZED_FOLDER,
  isSnoozedNavFolder,
  SNOOZED_FOLDER_LABEL,
} from "@/lib/snooze/constants";
import { isSnoozedListFolder, resolveSnoozedImapFolder } from "@/lib/snooze/snoozeNavFolder";
import type { AccountSettingsFocus } from "@/features/accounts/accountSettingsFocus";
import type { MailAccountSummary, MailMessageSummary } from "@/lib/types/mail";

const SEARCH_DEBOUNCE_MS = 350;

export function useMobileMailApp() {
  const nav = useMobileNavigation();
  const [composeOpen, setComposeOpen] = useState(false);
  const [draftSavePending, setDraftSavePending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsFocus, setSettingsFocus] = useState<AccountSettingsFocus>("navigation");
  const [settingsVersion, setSettingsVersion] = useState(0);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [folderStorageOpen, setFolderStorageOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [listFilter, setListFilter] = useState<InboxListFilter>(INBOX_FILTER_DEFAULT);
  const isSearchActive = searchInput.trim().length > 0;

  const mail = useMobileMail(nav.selectedUid, {
    pauseImapWhileCompose: composeOpen,
    searchQuery,
    searchInput,
    unreadOnly: listFilter.unreadOnly,
    syncEnabled: !isSearchActive,
  });

  /** Hub Simple / URL : ouvrir le message trouvé. */
  const mailDeepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (mailDeepLinkAppliedRef.current) return;
    if (mail.accountsLoading || mail.accounts.length === 0) return;
    const deep = readMailDeepLink();
    if (deep.uid == null) {
      mailDeepLinkAppliedRef.current = true;
      return;
    }
    mailDeepLinkAppliedRef.current = true;
    clearMailDeepLinkFromUrl();
    if (deep.accountId != null && mail.accounts.some((a) => a.id === deep.accountId)) {
      mail.selectAccount(deep.accountId);
    }
    if (deep.folder && deep.folder !== "INBOX") {
      mail.selectFolder(deep.folder);
    } else if (deep.folder === "INBOX") {
      mail.selectFolder("INBOX");
    }
    nav.openMessage(deep.uid, "fullscreen");
  }, [mail.accounts, mail.accountsLoading, mail.selectAccount, mail.selectFolder, nav]);

  const { orderedAccounts, moveAccount } = useOrderedMailAccounts(mail.accounts, {
    onApplyOrder: mail.applyAccountOrder,
    onReorderFailed: () => void mail.reloadAccounts(),
  });

  const isOutboxView = isOutboxNavFolder(mail.folder);
  const isSnoozedVirtualView = isSnoozedNavFolder(mail.folder);
  const accountSettings = useMemo(
    () => (mail.accountId != null ? readAccountSettings(mail.accountId) : undefined),
    [mail.accountId, mail.accountSettingsVersion, settingsVersion]
  );
  const isSnoozedImapView = useMemo(
    () =>
      !isSnoozedVirtualView &&
      isSnoozedListFolder(mail.folder, mail.folders, accountSettings) &&
      mail.folders.length > 0,
    [accountSettings, isSnoozedVirtualView, mail.folder, mail.folders]
  );
  const snoozeHoldImapFolder = useMemo(
    () => resolveSnoozedImapFolder(mail.folders, accountSettings),
    [accountSettings, mail.folders]
  );
  const isSnoozedImapFolderView = useMemo(
    () =>
      !isSnoozedVirtualView &&
      snoozeHoldImapFolder != null &&
      mail.folder === snoozeHoldImapFolder,
    [isSnoozedVirtualView, mail.folder, snoozeHoldImapFolder]
  );
  const isSnoozedView = isSnoozedVirtualView || isSnoozedImapFolderView;
  const isVirtualListView = isOutboxView || isSnoozedView;

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (mail.accountId == null) return;
    setListFilter(readInboxListFilter(mail.accountId, mail.folder));
  }, [mail.accountId, mail.folder]);

  const handleListFilterChange = useCallback(
    (next: InboxListFilter) => {
      setListFilter(next);
      if (mail.accountId != null) {
        writeInboxListFilter(mail.accountId, mail.folder, next);
      }
    },
    [mail.accountId, mail.folder]
  );

  const mailRefresh = useMailRefresh(
    mail.accountId ?? undefined,
    mail.folder,
    {
      reload: mail.reloadInbox,
      refreshInBackground: mail.syncInBackground,
      pullInboxNow: async () => {
        await mail.reloadInbox(true, { background: false, silent: false });
      },
    },
    {
      reload: mail.reloadFolders,
      refreshBadges: mail.refreshBadges,
      adjustFolderUnread: mail.adjustFolderUnread,
    }
  );

  const sendQueue = useMailSendQueue({
    accountId: mail.accountId ?? undefined,
    resolveSentFolder: (accountId) =>
      resolveSentImapFolder(mail.folders, readAccountSettings(accountId)) ?? undefined,
    onDelivered: (sentFolder) => {
      if (mail.accountId == null) return;
      const folder =
        sentFolder?.trim() || resolveSentImapFolder(mail.folders, accountSettings);
      if (!folder) return;
      mailRefresh.invalidateFolders({
        accountId: mail.accountId,
        folders: [folder],
        reloadListIfViewing: true,
        forceList: true,
      });
      void syncFolderHeaders(mail.accountId, folder);
    },
  });

  const scheduledSends = useMailScheduledSends({
    accountId: mail.accountId ?? undefined,
    enabled: isOutboxView,
    watchDue: mail.accountId != null,
  });
  const outboxQueueCount = outboxTotalCount(scheduledSends.count, sendQueue.queueCount);

  const outboxMessages = useMemo(() => {
    if (!mail.currentAccount) return [];
    return mergeOutboxSummaries(
      scheduledSends.toSummaries(mail.currentAccount.email),
      sendQueue.toSummaries(mail.currentAccount.email)
    );
  }, [mail.currentAccount, scheduledSends, sendQueue]);

  const mailSnooze = useMailSnooze(mail.accountId ?? undefined);
  const snoozedRows = useSnoozedListRows(
    mail.accountId ?? undefined,
    mailSnooze.snoozes,
    { holdFolder: snoozeHoldImapFolder, enabled: mail.accountId != null }
  );

  const reloadSnoozeList = mailSnooze.reload;

  useEffect(() => {
    if (!isSnoozedView || mail.accountId == null) return;
    void reloadSnoozeList({ silent: true });
  }, [isSnoozedView, mail.accountId, reloadSnoozeList]);

  const reloadScheduledSends = scheduledSends.reload;

  useEffect(() => {
    if (!isOutboxView || mail.accountId == null) return;
    const cached = getScheduledSendsCache(mail.accountId);
    if (cached?.some((entry) => entry.id < 1)) return;
    void reloadScheduledSends();
  }, [isOutboxView, mail.accountId, reloadScheduledSends]);

  const isDraftsFolder = useMemo(() => {
    if (isVirtualListView) return false;
    const meta = flattenFoldersForPicker(mail.folders).find((f) => f.name === mail.folder);
    const label = meta?.label ?? mail.folder;
    if (navFolderSlot(mail.folder, label) === "drafts") return true;
    return meta != null && isDraftsNavFolder(meta.name, meta.label);
  }, [isVirtualListView, mail.folder, mail.folders]);

  const isSentFolder = useMemo(() => {
    if (isVirtualListView) return false;
    const meta = flattenFoldersForPicker(mail.folders).find((f) => f.name === mail.folder);
    const label = meta?.label ?? mail.folder;
    return navFolderSlot(mail.folder, label) === "sent";
  }, [isVirtualListView, mail.folder, mail.folders]);

  const messageLabelsFolder = useMemo(() => {
    if (isOutboxView) return "INBOX";
    if (isSnoozedView && nav.selectedUid != null) {
      return mailSnooze.snoozes.find((s) => s.uid === nav.selectedUid)?.folder ?? "INBOX";
    }
    return mail.folder;
  }, [isOutboxView, isSnoozedView, mail.folder, mailSnooze.snoozes, nav.selectedUid]);

  const messageLabels = useMessageLabels(mail.accountId ?? undefined, messageLabelsFolder);

  const listDisplay = useInboxListDisplay({
    isOutboxView,
    isDraftsFolder,
    accountId: mail.accountId ?? undefined,
    selectedFolder: mail.folder,
    listFilter,
    inboxMessages: mail.messages,
    messageLabels,
    outboxMessages,
    mailSnooze,
    folders: mail.folders,
    accountSettings,
  });

  const searchDisplay = useMailSearchDisplay({
    accountId: mail.accountId ?? undefined,
    folder: mail.folder,
    isOutboxView,
    searchInput,
    searchQuery,
    serverSearchQuery: mail.searchResultQuery,
    serverMessages: listDisplay.filteredMessages,
    serverSyncing: mail.refreshing,
    applyLabels: (rows) => messageLabels.applyToMessages(rows),
  });

  const inboxListMessages = isOutboxView
    ? outboxMessages
    : isSnoozedView
      ? snoozedRows.messages.length > 0
        ? snoozedRows.messages
        : mailSnooze.snoozedMessages
      : searchDisplay.isSearchView
        ? searchDisplay.messages
        : listDisplay.displayMessages;

  const selectedPreview = useMemo(() => {
    if (nav.selectedUid == null) return null;
    return (
      inboxListMessages.find((m) => m.uid === nav.selectedUid) ??
      listDisplay.labeledMessages.find((m) => m.uid === nav.selectedUid) ??
      mail.messages.find((m) => m.uid === nav.selectedUid) ??
      null
    );
  }, [inboxListMessages, listDisplay.labeledMessages, mail.messages, nav.selectedUid]);

  const readerImapFolder = useMemo(() => {
    if (isOutboxView || nav.selectedUid == null) return mail.folder;
    if (isSnoozedView) {
      return (
        snoozedRows.bodyFolderByUid.get(nav.selectedUid) ??
        mailSnooze.snoozes.find((s) => s.uid === nav.selectedUid)?.holdFolder?.trim() ??
        mailSnooze.snoozes.find((s) => s.uid === nav.selectedUid)?.folder ??
        mail.folder
      );
    }
    return selectedPreview?.imapFolder ?? mail.folder;
  }, [
    isOutboxView,
    isSnoozedView,
    mail.folder,
    mailSnooze.snoozes,
    nav.selectedUid,
    selectedPreview?.imapFolder,
    snoozedRows.bodyFolderByUid,
  ]);

  const readerBodyUid = useMemo(() => {
    if (isOutboxView || nav.selectedUid == null) return nav.selectedUid;
    if (isSnoozedView) {
      return snoozedRows.bodyUidByUid.get(nav.selectedUid) ?? nav.selectedUid;
    }
    return selectedPreview?.imapBodyUid ?? nav.selectedUid;
  }, [
    isOutboxView,
    isSnoozedView,
    nav.selectedUid,
    selectedPreview?.imapBodyUid,
    snoozedRows.bodyUidByUid,
  ]);

  const readerSelectionKey =
    isOutboxView || nav.selectedUid == null ? null : String(nav.selectedUid);

  const pinnedReaderTargetRef = useRef<{
    key: string | null;
    bodyUid: number | null;
    imapFolder: string;
  }>({
    key: null,
    bodyUid: null,
    imapFolder: "INBOX",
  });

  if (readerSelectionKey !== pinnedReaderTargetRef.current.key) {
    pinnedReaderTargetRef.current = {
      key: readerSelectionKey,
      bodyUid: readerBodyUid,
      imapFolder: readerImapFolder,
    };
  }

  const pinnedReaderBodyUid =
    pinnedReaderTargetRef.current.key === readerSelectionKey
      ? pinnedReaderTargetRef.current.bodyUid
      : readerBodyUid;
  const pinnedReaderImapFolder =
    pinnedReaderTargetRef.current.key === readerSelectionKey
      ? pinnedReaderTargetRef.current.imapFolder
      : readerImapFolder;

  const reader = useMobileMessage(
    isOutboxView ? null : mail.accountId,
    pinnedReaderImapFolder,
    isOutboxView ? null : pinnedReaderBodyUid,
    isOutboxView ? null : selectedPreview
  );

  const compose = useMobileCompose({
    account: mail.currentAccount,
    accounts: orderedAccounts,
    folders: mail.folders,
    folder: mail.folder,
    messages: mail.messages,
    readerMessage: reader.message,
    removeMessages: mail.removeMessages,
    reloadInbox: mail.reloadInbox,
    reloadFolders: mail.reloadFolders,
    isOutboxView,
    onComposeOpenChange: setComposeOpen,
    onEnqueue: (payload) => sendQueue.enqueue(payload),
    onScheduleBegin: ({ accountId, sendAt, subject }) => {
      if (mail.accountId !== accountId) return;
      const placeholder = {
        id: -1,
        accountId,
        sendAt,
        status: "pending" as const,
        subject: subject.trim() || "(Sans objet)",
      };
      upsertScheduledSendCache(accountId, placeholder);
      scheduledSends.upsert(placeholder);
      mail.selectFolder(AURELIA_OUTBOX_FOLDER);
    },
    onScheduleEnd: () => {
      /* spinner mobile via sendingCount / placeholder */
    },
    onSavingDraftsChange: (pending) => {
      setDraftSavePending(pending);
    },
    onScheduled: (entry) => {
      if (entry && mail.accountId != null && mail.currentAccount) {
        removeScheduledSendPlaceholders(mail.accountId);
        upsertScheduledSendCache(mail.accountId, entry);
        scheduledSends.upsert(entry);
        mail.selectFolder(AURELIA_OUTBOX_FOLDER);
        const summaries = scheduledSendsToSummaries(
          getScheduledSendsCache(mail.accountId) ?? [entry],
          mail.currentAccount.email
        );
        const row = summaries.find((m) => m.scheduledSendId === entry.id);
        if (row) nav.openMessage(row.uid, "fullscreen");
      }
    },
  });

  const setSelectedUid = useCallback(
    (uid: number | null) => {
      if (uid == null) nav.backToInbox();
      else nav.openMessage(uid, "fullscreen");
    },
    [nav]
  );

  const resolveMessageFolder = useCallback(
    (uid: number) => {
      if (isSnoozedView) {
        return (
          snoozedRows.bodyFolderByUid.get(uid) ??
          mailSnooze.snoozes.find((s) => s.uid === uid)?.folder ??
          mail.folder
        );
      }
      const row =
        inboxListMessages.find((m) => m.uid === uid) ??
        listDisplay.labeledMessages.find((m) => m.uid === uid);
      return row?.imapFolder ?? mail.folder;
    },
    [
      inboxListMessages,
      isSnoozedView,
      listDisplay.labeledMessages,
      mail.folder,
      mailSnooze.snoozes,
      snoozedRows.bodyFolderByUid,
    ]
  );

  const bulk = useInboxBulkActions({
    markReadOnOpenDelayMs: 0,
    advanceSelectionAfterDelete: false,
    account: mail.currentAccount ?? undefined,
    isOutboxView,
    selectedFolder: mail.folder,
    selectedUid: nav.selectedUid,
    setSelectedUid,
    displayMessages: inboxListMessages,
    labeledMessages: listDisplay.labeledMessages,
    inboxMessages: mail.messages,
    inbox: {
      removeMessages: mail.removeMessages,
      markMessageSeen: mail.markMessageSeen,
      markMessageUnread: mail.markMessageUnread,
      setMessagesSeen: mail.setMessagesSeen,
      markMessageFlagged: mail.markMessageFlagged,
    },
    messageLabels,
    mailSnooze,
    isSnoozedView,
    isDraftsFolder,
    resolveMessageFolder,
    inboxListActive: !isVirtualListView,
    adjustFolderUnread: mail.adjustFolderUnread,
    invalidateFolders: mailRefresh.invalidateFolders,
    recoverAfterMutationError: mailRefresh.recoverAfterMutationError,
    openDraftForEdit: compose.openDraftForEdit,
    openComposeForUid: compose.openComposeForUid,
    resolveSnoozeHoldFolder: () =>
      resolveSnoozedImapFolder(mail.folders, accountSettings),
    resolveTrashFolder: (accountId: number) => {
      const folders = accountId === mail.accountId ? mail.folders : mail.folders;
      const settings = readAccountSettings(accountId);
      return resolveTrashImapFolder(folders, settings);
    },
    onSnoozeFolderRequired: () => {
      window.alert(
        "Ouvrez les paramètres du compte → Organisation des dossiers, puis créez ou branchez un dossier « En attente »."
      );
    },
  });

  useEffect(() => {
    bulk.clearSelection();
  }, [bulk.clearSelection, mail.accountId, mail.folder]);

  const actions = useMobileActions({
    account: mail.currentAccount,
    accountId: mail.accountId,
    folder: mail.folder,
    isOutboxView,
    isSnoozedView,
    isDraftsFolder,
    inboxListActive: !isVirtualListView,
    displayMessages: inboxListMessages,
    labeledMessages: listDisplay.labeledMessages,
    inbox: {
      removeMessages: mail.removeMessages,
      markMessageSeen: mail.markMessageSeen,
      markMessageUnread: mail.markMessageUnread,
      setMessagesSeen: mail.setMessagesSeen,
      markMessageFlagged: mail.markMessageFlagged,
    },
    messageLabels,
    mailSnooze,
    compose: {
      openComposeForUid: compose.openComposeForUid,
      openDraftForEdit: compose.openDraftForEdit,
    },
    refresh: {
      invalidateFolders: mailRefresh.invalidateFolders,
      recoverAfterMutationError: mailRefresh.recoverAfterMutationError,
    },
    adjustFolderUnread: mail.adjustFolderUnread,
    resolveSnoozeHoldFolder: () =>
      resolveSnoozedImapFolder(mail.folders, accountSettings),
    resolveTrashFolder: (accountId: number) => {
      const settings = readAccountSettings(accountId);
      return resolveTrashImapFolder(mail.folders, settings);
    },
    onSnoozeFolderRequired: () => {
      window.alert(
        "Ouvrez les paramètres du compte → Organisation des dossiers, puis créez ou branchez un dossier « En attente »."
      );
    },
    onRecover: mail.refresh,
    onCloseReader: nav.backToInbox,
    onArchiveToCoffre: (message) => {
      bulk.openArchiveToCoffre([
        {
          uid: message.uid,
          accountId: message.accountId ?? mail.accountId ?? undefined,
        },
      ]);
    },
  });

  const selectedOutboxRow = useMemo(() => {
    if (!isOutboxView || nav.selectedUid == null) return null;
    return outboxMessages.find((m) => m.uid === nav.selectedUid) ?? null;
  }, [isOutboxView, nav.selectedUid, outboxMessages]);

  const selectedOutboxItem = useMemo(() => {
    if (!selectedOutboxRow?.outboxId) return null;
    return sendQueue.getItemById(selectedOutboxRow.outboxId);
  }, [selectedOutboxRow, sendQueue]);

  const selectedScheduledSend = useMemo(() => {
    if (!selectedOutboxRow?.scheduledSendId) return null;
    return scheduledSends.getById(selectedOutboxRow.scheduledSendId);
  }, [selectedOutboxRow, scheduledSends]);

  const selectedReaderFlags = useMemo(() => {
    if (isOutboxView || nav.selectedUid == null) {
      return { flagged: false, favorite: false };
    }
    const row =
      inboxListMessages.find((m) => m.uid === nav.selectedUid) ??
      listDisplay.labeledMessages.find((m) => m.uid === nav.selectedUid);
    const label = messageLabels.labels.get(nav.selectedUid);
    return {
      flagged: row?.flagged ?? false,
      favorite: label?.favorite ?? row?.favorite ?? false,
    };
  }, [inboxListMessages, isOutboxView, listDisplay.labeledMessages, messageLabels.labels, nav.selectedUid]);

  const folderTitle = useMemo(() => {
    if (isSnoozedVirtualView || isSnoozedImapView) return SNOOZED_FOLDER_LABEL;
    if (isOutboxView) {
      return resolveOutboxNavLabel({
        queueCount: sendQueue.queueCount,
        sendingCount: sendQueue.sendingCount + scheduledSends.sendingCount,
        failedCount: sendQueue.failedCount,
        scheduledCount: scheduledSends.count,
      });
    }
    const match = flattenFoldersForPicker(mail.folders).find((f) => f.name === mail.folder);
    const label = match?.label ?? mail.folder;
    if (draftSavePending && isDraftsNavFolder(mail.folder, label)) {
      return DRAFTS_SAVING_FOLDER_LABEL;
    }
    const slot = navFolderSlot(mail.folder, label);
    if (slot) return NAV_FOLDER_LABELS[slot];
    return label;
  }, [
    draftSavePending,
    isOutboxView,
    isSnoozedImapView,
    isSnoozedVirtualView,
    mail.folder,
    mail.folders,
    sendQueue.failedCount,
    sendQueue.queueCount,
    scheduledSends.count,
    scheduledSends.sendingCount,
    sendQueue.sendingCount,
  ]);

  const handlePrefetchMessage = useCallback(
    (msg: MailMessageSummary) => {
      if (isOutboxView || mail.accountId == null || msg.uid <= 0) return;
      const bodyFolder = msg.imapFolder ?? mail.folder;
      const bodyUid = msg.imapBodyUid ?? msg.uid;
      void warmMessageBodyForOpen(mail.accountId, bodyFolder, bodyUid, msg);
    },
    [isOutboxView, mail.accountId, mail.folder]
  );

  const handleSelectMessage = useCallback(
    (uid: number, event?: React.MouseEvent) => {
      // Ouverture immédiate — le lecteur charge (mémoire / DD02), pas d’await au tap.
      bulk.handleSelectMessage(uid, event);
    },
    [bulk.handleSelectMessage]
  );

  const handleSelectFolder = useCallback(
    (name: string) => {
      const prevFolder = mail.folder;
      mail.selectFolder(name);
      nav.closeNav();
      if (nav.selectedUid != null) nav.backToInbox();
      setSearchInput("");
      setSearchQuery("");
      if (
        name !== prevFolder &&
        !isOutboxNavFolder(name) &&
        !isSnoozedNavFolder(name)
      ) {
        void mail.reloadInbox(false, { background: false, silent: false });
      }
    },
    [mail, nav]
  );

  /** Clic compte : INBOX + ferme drawer / message ouvert (comme un dossier). */
  const handleSelectAccount = useCallback(
    (id: number) => {
      const prevAccountId = mail.accountId;
      const prevFolder = mail.folder;
      mail.selectAccount(id);
      nav.closeNav();
      if (nav.selectedUid != null) nav.backToInbox();
      setSearchInput("");
      setSearchQuery("");
      if (id !== prevAccountId || prevFolder !== "INBOX") {
        void mail.reloadInbox(false, { background: false, silent: false });
      }
    },
    [mail, nav]
  );

  const handleOpenSettings = useCallback((focus: AccountSettingsFocus = "navigation") => {
    nav.closeNav();
    setSettingsFocus(focus);
    setSettingsOpen(true);
  }, [nav]);

  const handleAccountSettingsSaved = useCallback(() => {
    setSettingsVersion((v) => v + 1);
  }, []);

  const handleAccountUpdated = useCallback(
    (accountId: number, patch: Partial<MailAccountSummary>) => {
      mail.patchAccount(accountId, patch);
      setSettingsVersion((v) => v + 1);
    },
    [mail]
  );

  const handleDefaultAccountChanged = useCallback(
    (accountId: number) => {
      mail.selectAccount(accountId);
      if (nav.selectedUid != null) nav.backToInbox();
    },
    [mail, nav]
  );

  const handleDisconnectAccount = useCallback(
    async (accountId: number) => {
      setDisconnecting(true);
      try {
        const res = await deleteMailAccount(accountId);
        if (!res.ok) {
          window.alert(res.message);
          return;
        }

        setSettingsOpen(false);
        setSettingsFocus("navigation");
        nav.backToInbox();
        mail.removeAccount(accountId);
        void clearAccountClientMailCache(accountId).catch(() => undefined);
        void mail.reloadAccounts();
      } finally {
        setDisconnecting(false);
      }
    },
    [mail, nav]
  );

  const handleAccountConnected = useCallback(
    async (account?: MailAccountSummary) => {
      await mail.reloadAccounts();
      setConnectModalOpen(false);
      nav.closeNav();
      if (account?.id == null) return;

      mail.selectAccount(account.id);
      if (nav.selectedUid != null) nav.backToInbox();

      try {
        const foldersRes = await fetchMailFolders({ accountId: account.id, refresh: true });
        const folderList =
          foldersRes.ok && foldersRes.data.folders?.length ? foldersRes.data.folders : [];
        if (folderList.length === 0) return;

        const pickedSent = ensureSentFolderOverride(account.id, folderList);
        const aureliaMap =
          foldersRes.ok && foldersRes.data.aureliaFolders
            ? foldersRes.data.aureliaFolders
            : null;
        if (aureliaMap && Object.keys(aureliaMap).length > 0) {
          setSettingsVersion((v) => v + 1);
          showMailToast(
            "Dossiers Aurélia prêts (Envoyés, Brouillons, En attente, Indésirables, Corbeille).",
            "info",
            6000
          );
        } else if (pickedSent) {
          setSettingsVersion((v) => v + 1);
          const label =
            flattenFoldersForPicker(folderList).find((f) => f.name === pickedSent)?.label ??
            pickedSent;
          showMailToast(`Dossier Envoyés associé à « ${label} ».`, "info");
        }

        if (!hasSeenFolderImapNudge(account.id)) {
          const overrides = readAccountSettings(account.id).folderSlotOverrides;
          if (criticalFolderSlotsNeedAttention(folderList, overrides)) {
            markFolderImapNudgeSeen(account.id);
            showMailToast(
              "Vérifiez le branchement des dossiers IMAP (Envoyés, En attente…).",
              "info",
              7000
            );
            handleOpenSettings("navigation");
          }
        }
      } catch {
        /* ignore — pastilles restent dispo dans les paramètres */
      }
    },
    [handleOpenSettings, mail, nav]
  );

  const virtualNavEntries = useMemo(() => {
    const entries: { name: string; label: string; badge?: number }[] = [];
    if (outboxQueueCount > 0) {
      entries.push({
        name: AURELIA_OUTBOX_FOLDER,
        label: resolveOutboxNavLabel({
          queueCount: sendQueue.queueCount,
          sendingCount: sendQueue.sendingCount + scheduledSends.sendingCount,
          failedCount: sendQueue.failedCount,
          scheduledCount: scheduledSends.count,
        }),
        badge: outboxQueueCount,
      });
    }
    if (mailSnooze.snoozes.length > 0) {
      entries.push({
        name: AURELIA_SNOOZED_FOLDER,
        label: SNOOZED_FOLDER_LABEL,
        badge: mailSnooze.snoozes.length,
      });
    }
    return entries;
  }, [
    mailSnooze.snoozes.length,
    outboxQueueCount,
    sendQueue.failedCount,
    sendQueue.queueCount,
    scheduledSends.count,
    scheduledSends.sendingCount,
    sendQueue.sendingCount,
  ]);

  return {
    nav,
    mail,
    compose,
    composeOpen,
    sendQueue,
    scheduledSends,
    outboxQueueCount,
    reader,
    actions,
    bulk,
    folderTitle,
    inboxListMessages,
    listFilter,
    handleListFilterChange,
    searchInput,
    setSearchInput,
    searchDisplay,
    isVirtualListView,
    isOutboxView,
    isDraftsFolder,
    isSentFolder,
    isSnoozedView,
    isSearchActive,
    handleSelectMessage,
    handlePrefetchMessage,
    handleSelectFolder,
    handleSelectAccount,
    handleOpenSettings,
    handleAccountSettingsSaved,
    handleAccountUpdated,
    handleDefaultAccountChanged,
    handleDisconnectAccount,
    handleAccountConnected,
    connectModalOpen,
    setConnectModalOpen,
    orderedAccounts,
    moveAccount,
    settingsOpen,
    setSettingsOpen,
    settingsFocus,
    setSettingsFocus,
    settingsVersion,
    disconnecting,
    virtualNavEntries,
    selectedOutboxItem,
    selectedScheduledSend,
    selectedPreview,
    selectedReaderFlags,
    readerImapFolder: pinnedReaderImapFolder,
    moveFolders: flattenFoldersForPicker(mail.folders),
    folderStorageOpen,
    setFolderStorageOpen,
  };
}
